import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { userApi, apiError } from "@/lib/userApi";
import { useUserAuth } from "@/context/UserAuthContext";
import { toast } from "sonner";
import { ArrowRight, MailCheck, RotateCcw } from "lucide-react";
import { CircuitBg } from "@/components/CircuitBg";
import { GlassField, GlassButton, GlassDivider, GoogleIcon } from "@/components/GlassBits";
import { playForgeGate } from "@/components/ForgeGate";
import { pickCoverPhrase } from "@/lib/coverPhrases";

export default function Register() {
  const [params] = useSearchParams();
  const pending = params.get("verificar") || "";
  const ref = params.get("ref") || localStorage.getItem("df_ref") || "";
  useEffect(() => { if (params.get("ref")) localStorage.setItem("df_ref", params.get("ref")); }, [params]);
  const [step, setStep] = useState(pending ? 2 : 1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState(pending);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(null);
  const [phrase] = useState(pickCoverPhrase);
  const { signIn } = useUserAuth();
  const navigate = useNavigate();

  const register = async () => {
    if (!email.trim()) return toast.error("Escribe tu correo");
    if (password.length < 8) return toast.error("La contraseña necesita al menos 8 caracteres");
    setBusy("register");
    try {
      const { data } = await userApi.post("/user/auth/register",
        { email: email.trim(), password, name: name.trim(), ref });
      if (data.email_sent === false) {
        toast.warning("Creamos tu cuenta, pero no pudimos enviar el correo. Revisa la dirección y pulsa «Reenviar código».");
      } else {
        toast.success("Te enviamos un código de 6 dígitos a tu correo");
      }
      setStep(2);
    } catch (e) {
      toast.error(apiError(e, "No se pudo crear la cuenta"));
    } finally { setBusy(null); }
  };

  const verify = async () => {
    if (code.trim().length !== 6) return toast.error("El código tiene 6 dígitos");
    setBusy("verify");
    try {
      const { data } = await userApi.post("/user/auth/verify", { email: email.trim(), code: code.trim() });
      localStorage.removeItem("df_ref");
      signIn(data.user);
      toast.success(`Cuenta lista. Tienes ${data.user.credits} créditos de regalo.`);
      await playForgeGate({
        title: `BIENVENIDO${data.user.name ? `, ${data.user.name.toUpperCase()}` : ""}`,
        subtitle: "A LA COMUNIDAD DE DIGITAL FORGE",
      });
      navigate("/muro");
    } catch (e) {
      toast.error(apiError(e, "Código incorrecto"));
    } finally { setBusy(null); }
  };

  const resend = async () => {
    setBusy("resend");
    try {
      await userApi.post("/user/auth/resend-code", { email: email.trim() });
      toast.success("Código reenviado");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(null); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/muro";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center circuit-bg theme-noir py-14" style={{ backgroundColor: "#05070C" }}>
      <CircuitBg />
      <div className="circuit-veil" aria-hidden="true" />
      <div className="w-full max-w-md px-6 animate-fade-up">
        <div className="flex flex-col items-center mb-7" style={{ textShadow: "0 2px 18px rgba(0,0,0,0.9)" }}>
          <img src="/logo-digital-forge.jpg" alt="Digital Forge" data-testid="brand-logo"
            className="w-56 h-56 sm:w-72 sm:h-72 lg:w-80 lg:h-80 rounded-full object-cover mx-auto mb-5 border border-white/10 brand-hue is-logo" />
          <div className="font-heading text-lg md:text-xl tracking-[0.42em] text-amber-300/90 mb-2.5">DIGITAL FORGE</div>
          <h1 className="cover-title brand-hue text-4xl sm:text-5xl lg:text-6xl" data-testid="cover-headline">
            {phrase}
          </h1>
          <p className="text-base md:text-lg text-white/80 mt-3 font-heading">Crear cuenta</p>
        </div>

        <div className="glass-card forge-card p-7" data-testid="register-card">
          {step === 1 ? (
            <>
              <div className="space-y-4">
                <GlassField data-testid="register-name-input" label="Tu nombre" value={name}
                  placeholder="Cómo quieres que te llame" onChange={(e) => setName(e.target.value)} />
                <GlassField data-testid="register-email-input" label="Correo" type="email" value={email}
                  autoComplete="email" placeholder="tucorreo@ejemplo.com"
                  onChange={(e) => setEmail(e.target.value)} />
                <GlassField data-testid="register-password-input" label="Contraseña" type="password"
                  value={password} autoComplete="new-password" placeholder="Mínimo 8 caracteres"
                  hint="Te regalamos créditos para tu primera edición."
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && register()} />
              </div>
              <div className="space-y-2.5 mt-6">
                <GlassButton data-testid="register-submit-btn" tone="red" loading={busy === "register"}
                  onClick={register}>Crear mi cuenta <ArrowRight className="w-4 h-4" /></GlassButton>
                <GlassButton data-testid="register-google-btn" tone="neutral" onClick={googleLogin}>
                  <GoogleIcon /> Continuar con Google
                </GlassButton>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-white/80 text-sm mb-1">
                <MailCheck className="w-4 h-4 text-red-400" /> Verifica tu correo
              </div>
              <p className="text-xs text-white/50 mb-5">
                Escribe el código de 6 dígitos que enviamos a{" "}
                <span className="font-mono text-white/80">{email}</span>.
              </p>
              <GlassField data-testid="verify-code-input" label="Código" value={code} maxLength={6}
                inputClass="font-mono text-center text-2xl tracking-[0.4em]" placeholder="000000"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => e.key === "Enter" && verify()} />
              <div className="space-y-2.5 mt-6">
                <GlassButton data-testid="verify-submit-btn" tone="red" loading={busy === "verify"}
                  onClick={verify}>Verificar y entrar</GlassButton>
                <GlassButton data-testid="verify-resend-btn" tone="ghost" icon={RotateCcw}
                  loading={busy === "resend"} onClick={resend}>Reenviar código</GlassButton>
              </div>
            </>
          )}

          <GlassDivider>ya tengo cuenta</GlassDivider>
          <p className="text-center text-xs text-white/55">
            <Link to="/acceso" data-testid="go-login-link" className="text-red-400 hover:text-red-300 font-semibold">
              Iniciar sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
